import { defineComponent as _defineComponent } from 'vue';
import { renderSlot as _renderSlot, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = { class: "week-list" };
const _hoisted_2 = { class: "week-list__header" };
const _hoisted_3 = { class: "week-list__title" };
const _hoisted_4 = { class: "week-list__nav" };
const _hoisted_5 = {
    key: 0,
    class: "week-list__days"
};
const _hoisted_6 = {
    key: 1,
    class: "week-list__no-events"
};
const _hoisted_7 = { class: "week-list__no-events-message" };
import { ref, watch } from 'vue';
import moment from "moment";
import Icon from "@astina/paloma-common-ui/src/components/Icon.vue";
import deliveryRepository from "../services/DeliveryRepository";
import trans, { transExists } from "@astina/paloma-common-ui/src/services/Translator";
import { useRouter } from "vue-router";
import PendingOrdersListSection from "./PendingOrdersListSection.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'PendingOrdersList',
    props: {
        events: {},
        date: {},
        now: {},
        minDate: {},
        maxDate: {},
        numberOfWeeks: {},
        noEventsMessage: {},
        noHighlightNow: { type: Boolean }
    },
    emits: [
        'change',
        'select-event'
    ],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const router = useRouter();
        const from = ref(initialStartDate());
        const until = ref(calculateUntilDate());
        const hasPrev = ref(true);
        const hasNext = ref(true);
        const days = ref([]);
        const visibleEvents = ref([]);
        // used for Excel export
        const visibleDeliveries = ref(new Map());
        // used for displaying
        const deliveries = ref([]);
        init();
        watch(() => props.date, () => {
            from.value = initialStartDate();
            init();
        });
        watch(() => props.events, init);
        function init() {
            until.value = calculateUntilDate();
            days.value = [];
            for (let i = 0; i < (props.numberOfWeeks ?? 1) * 7; i++) {
                let day = createDay(i);
                if (day.events.length > 0) {
                    days.value.push(day);
                }
            }
            deliveries.value = [];
            visibleEvents.value = [];
            visibleDeliveries.value.clear();
            days.value.forEach(day => {
                day.events.forEach(event => {
                    visibleEvents.value.push(event);
                    loadDelivery(event);
                });
            });
            hasPrev.value = props.minDate === undefined || from.value.getTime() > props.minDate.getTime();
            hasNext.value = props.maxDate === undefined || getNext().getTime() < props.maxDate.getTime();
        }
        function createDay(dayNumber) {
            const now = moment(props.now ?? new Date());
            const date = moment(from.value).add(dayNumber, 'days');
            const number = date.format('d');
            const events = props.events
                .filter(event => {
                return moment(event.date).isSame(date, 'day');
            })
                .map(event => {
                if (!event.classes) {
                    event.classes = [];
                }
                event.classes.push('week-list__event--status-' + event.status || 'default');
                return event;
            });
            const classes = {
                'week-list__day': true,
                'week-list__day--active': !props.noHighlightNow && date.isSame(moment(props.now ?? new Date()), 'day'),
                'week-list__day--has-events': events.length > 0,
                'week-list__day--no-events': events.length === 0,
                'week-list__day--past': date.isBefore(now.startOf('day')),
                'week-list__day--today': date.isSame(now, 'day'),
                'week-list__day--future': date.isAfter(now.endOf('day')),
            };
            classes['week-list__day--' + number] = true;
            const format = 'dddd, DD. MMM YYYY';
            return {
                number: number,
                name: date.format(format),
                date: date.toDate(),
                events: events,
                active: false,
                classes: classes
            };
        }
        function loadDelivery(event) {
            if (!event || !event.route || !event.route.params || !event.route.params['id']) {
                return;
            }
            const deliveryId = eventDeliveryIdParam(event);
            deliveryRepository.get(deliveryId).then((delivery) => {
                deliveries.value.push(delivery);
                visibleDeliveries.value.set(deliveryId, delivery);
            });
        }
        function eventDeliveryIdParam(event) {
            return event?.route?.params['id'];
        }
        function deliveriesForDay(day) {
            const date = moment(day.date).format('YYYY-MM-DD');
            return deliveries.value.filter(delivery => delivery.targetDate === date);
        }
        function prev() {
            if (!hasPrev.value) {
                return;
            }
            from.value = getPrev();
            onChange();
        }
        function next() {
            if (!hasNext.value) {
                return;
            }
            from.value = getNext();
            onChange();
        }
        function reset() {
            from.value = initialStartDate();
            onChange();
        }
        function initialStartDate() {
            const start = moment(props.date ?? new Date()).startOf('week');
            return start.toDate();
        }
        function calculateUntilDate() {
            return moment(from.value)
                .add((7 * (props.numberOfWeeks ?? 1)) - 1, 'days')
                .endOf('day')
                .toDate();
        }
        function onChange() {
            init();
            emit('change', from.value, until.value);
        }
        function getPrev() {
            return moment(from.value).subtract(props.numberOfWeeks ?? 1, 'weeks').toDate();
        }
        function getNext() {
            return moment(from.value).add(props.numberOfWeeks ?? 1, 'weeks').toDate();
        }
        function exportAsXlsx() {
            //headers
            const orderHeader = trans('pending_orders.export.order');
            const dateHeader = trans('pending_orders.export.date');
            const skuHeader = trans('pending_orders.export.sku');
            const nameHeader = trans('pending_orders.export.name');
            const quantityHeader = trans('pending_orders.export.quantity');
            const unitHeader = trans('pending_orders.export.unit');
            const linkHeader = trans('pending_orders.export.link');
            //data
            const data = [];
            for (const [eventTitle, delivery] of visibleDeliveries.value.entries()) {
                const event = visibleEvents.value.find(e => e.title === eventTitle);
                let link = '';
                if (event?.route) {
                    const resolved = router.resolve({
                        name: event.route.name,
                        params: event.route.params
                    });
                    link = `${window.location.origin}${resolved.href}`;
                }
                for (const item of delivery.items) {
                    data.push({
                        [orderHeader]: delivery.deliveryNumber,
                        [dateHeader]: moment(delivery.targetDate).format('DD.MM.YYYY'),
                        [skuHeader]: item.sku,
                        [quantityHeader]: item.quantity,
                        [unitHeader]: transExists("unit." + item.quantityUnit) ? trans("unit." + item.quantityUnit) : item.quantityUnit,
                        [nameHeader]: item.name,
                        [linkHeader]: link
                    });
                }
            }
            //export via ExcelExportService
            import('../services/ExcelExportService').then(({ exportJsonAsXlsx }) => {
                const fromStr = moment(from.value).format('DDMMYYYY');
                const untilStr = moment(until.value).format('DDMMYYYY');
                const sheetName = trans('pending_orders.export.orders');
                const filename = `${sheetName}_${fromStr}-${untilStr}.xlsx`;
                //find linkHeader column index
                const headers = Object.keys(data[0] ?? {});
                const linkColumnIndex = headers.findIndex(h => h === linkHeader);
                exportJsonAsXlsx(data, sheetName, filename, linkColumnIndex >= 0 ? linkColumnIndex : null);
            });
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                        _renderSlot(_ctx.$slots, "title", {}, () => [
                            _createTextVNode(_toDisplayString(_ctx.$t('week.from_until', [_ctx.$date(from.value, from.value.getFullYear() === until.value.getFullYear()
                                    ? (from.value.getMonth() === until.value.getMonth()
                                        ? 'Do' : 'Do MMMM') : 'Do MMMM YYYY'), _ctx.$date(until.value, 'Do MMMM YYYY')])), 1)
                        ])
                    ]),
                    _cache[3] || (_cache[3] = _createTextVNode()),
                    _createElementVNode("div", _hoisted_4, [
                        (visibleEvents.value.length > 0)
                            ? (_openBlock(), _createElementBlock("a", {
                                key: 0,
                                class: "week-list__nav-export",
                                onClick: _withModifiers(exportAsXlsx, ["prevent"]),
                                href: ""
                            }, [
                                _createVNode(Icon, { name: "file-excel" }),
                                _createTextVNode(" " + _toDisplayString(_ctx.$t('pending_orders.export_as_excel')), 1)
                            ]))
                            : _createCommentVNode("", true),
                        _cache[0] || (_cache[0] = _createTextVNode()),
                        (hasPrev.value)
                            ? (_openBlock(), _createElementBlock("a", {
                                key: 1,
                                class: "week-list__nav-link",
                                onClick: _withModifiers(prev, ["prevent"]),
                                href: ""
                            }, [
                                _createVNode(Icon, { name: "angle-left" })
                            ]))
                            : _createCommentVNode("", true),
                        _cache[1] || (_cache[1] = _createTextVNode()),
                        (hasPrev.value || hasNext.value)
                            ? (_openBlock(), _createElementBlock("a", {
                                key: 2,
                                class: "week-list__nav-link week-list__nav-link--reset",
                                onClick: _withModifiers(reset, ["prevent"]),
                                href: ""
                            }, [
                                _createVNode(Icon, { name: "dot-circle" })
                            ]))
                            : _createCommentVNode("", true),
                        _cache[2] || (_cache[2] = _createTextVNode()),
                        (hasNext.value)
                            ? (_openBlock(), _createElementBlock("a", {
                                key: 3,
                                class: "week-list__nav-link",
                                onClick: _withModifiers(next, ["prevent"]),
                                href: ""
                            }, [
                                _createVNode(Icon, { name: "angle-right" })
                            ]))
                            : _createCommentVNode("", true)
                    ])
                ]),
                _cache[4] || (_cache[4] = _createTextVNode()),
                (visibleEvents.value.length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(days.value, (day, d) => {
                            return (_openBlock(), _createElementBlock("div", {
                                key: d,
                                class: _normalizeClass(day.classes)
                            }, [
                                _createVNode(PendingOrdersListSection, {
                                    day: day,
                                    deliveries: deliveriesForDay(day)
                                }, null, 8, ["day", "deliveries"])
                            ], 2));
                        }), 128))
                    ]))
                    : _createCommentVNode("", true),
                _cache[5] || (_cache[5] = _createTextVNode()),
                (__props.noEventsMessage && visibleEvents.value.length === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                        _createElementVNode("span", _hoisted_7, _toDisplayString(_ctx.$t(__props.noEventsMessage, [_ctx.$date(from.value, from.value.getFullYear() === until.value.getFullYear() ? (from.value.getMonth() === until.value.getMonth() ? 'Do' : 'Do MMMM') : 'Do MMMM YYYY'), _ctx.$date(until.value, 'Do MMMM YYYY')])), 1)
                    ]))
                    : _createCommentVNode("", true)
            ]));
        };
    }
});
